import { z } from 'zod';
import type { AssessmentConfig, AssessmentTask } from '../assessment/schema.js';
import { JobInputSchema, type JobInput } from '../contracts.js';
import { defaultApplySettings, defaultJobMessages, type Fact, type KnockoutRule } from '../interview/config.js';
import { LANGUAGE_CODES, type LanguageCode } from '../lang.js';
import type { RoleTemplate } from './types.js';

/**
 * What a company tells us about the job it is hiring for. With a role from the library (roleId) this
 * is enough to build the whole job without AI; the AI draft reads it too, with the pasted job description.
 */
export const JobRequirementsSchema = z
  .object({
    /** A role from the library; empty for a role described only in the requirements. */
    roleId: z.string().max(60).optional(),
    title: z.string().trim().min(2).max(120),
    companyName: z.string().trim().min(1).max(80),
    /** A line or two about the company, for the job description. */
    aboutCompany: z.string().trim().max(1500).optional(),
    location: z.string().trim().min(2).max(120),
    workMode: z.enum(['On-site', 'Hybrid', 'Remote']),
    experience: z.object({ min: z.number().min(0).max(40), max: z.number().min(0).max(45) }),
    /** Highest expected CTC in the budget, in lakhs a year. */
    budgetMaxLpa: z.number().min(0).max(500).optional(),
    maxNoticeDays: z.number().int().min(0).max(365).optional(),
    /** Rotational shifts or weekends; unset means the role's usual pattern. */
    shifts: z.boolean().optional(),
    /** Languages the candidate may choose for the AI interview; the first is the default. */
    languages: z.array(z.enum(LANGUAGE_CODES as [LanguageCode, ...LanguageCode[]])).min(1),
    /** Skills, tools, qualifications or licences the company needs. */
    mustHaves: z.string().trim().max(2000).optional(),
    /** The company's own job description, pasted. */
    jobDescription: z.string().trim().max(12000).optional(),
    interviewerName: z.string().trim().min(1).max(40).optional(),
  })
  .refine((r) => r.experience.min <= r.experience.max, { message: 'Minimum experience is above the maximum', path: ['experience'] });
export type JobRequirements = z.infer<typeof JobRequirementsSchema>;

/** A deep copy of plain data (templates are shared; jobs get their own). */
export function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Fact keys the builder always adds; a role's own facts must not reuse them. */
export const COMMON_FACT_KEYS = [
  'current_role',
  'total_experience_years',
  'relevant_experience_years',
  'current_location',
  'willing_to_relocate',
  'notice_period_days',
  'current_ctc_lpa',
  'expected_ctc_lpa',
  'shift_flexible',
  'languages_spoken',
  'mother_tongue',
] as const;

/** The facts every interview establishes, whatever the role. */
export function commonFacts(relevantExperience: string, req: Pick<JobRequirements, 'workMode' | 'location'>, shifts: boolean): Fact[] {
  const facts: Fact[] = [
    { key: 'current_role', label: 'Current role', type: 'text', ask: 'Current designation and company (for a fresher: what they are doing now).', required: true },
    { key: 'total_experience_years', label: 'Total experience', type: 'number', unit: 'years', ask: 'Total years of work experience.', required: true },
    { key: 'relevant_experience_years', label: 'Relevant experience', type: 'number', unit: 'years', ask: `Years of experience in ${relevantExperience}.`, required: true },
    { key: 'current_location', label: 'Current location', type: 'text', ask: 'City they live in now.', required: true },
  ];
  if (req.workMode !== 'Remote') {
    facts.push({
      key: 'willing_to_relocate',
      label: 'Open to the job location',
      type: 'boolean',
      ask: `Whether they can work from ${req.location}${req.workMode === 'Hybrid' ? ' on office days' : ''} (ask only if they live elsewhere).`,
      required: true,
    });
  }
  facts.push(
    { key: 'notice_period_days', label: 'Notice period', type: 'number', unit: 'days', ask: 'Notice period in days, and whether it is negotiable.', required: true },
    { key: 'current_ctc_lpa', label: 'Current CTC', type: 'number', unit: 'LPA', ask: 'Current annual CTC in lakhs (0 for a fresher).', required: true },
    { key: 'expected_ctc_lpa', label: 'Expected CTC', type: 'number', unit: 'LPA', ask: 'Expected annual CTC in lakhs.', required: true },
  );
  if (shifts) {
    facts.push({ key: 'shift_flexible', label: 'Open to shifts', type: 'boolean', ask: 'Whether they are comfortable with rotational shifts, including some weekends.', required: true });
  }
  // The skills assessment runs role-plays in the languages they speak, except their mother tongue.
  facts.push(
    { key: 'languages_spoken', label: 'Languages spoken', type: 'text', ask: 'Every language they can comfortably hold a conversation in (list them all).', required: true },
    { key: 'mother_tongue', label: 'Mother tongue', type: 'text', ask: 'Their mother tongue (first language).', required: true },
  );
  return facts;
}

/** Rules from the company's own limits: all soft (a person reviews), except not being able to work from the location. */
export function requirementKnockouts(req: JobRequirements, facts: Fact[]): KnockoutRule[] {
  const has = new Set(facts.map((f) => f.key));
  const rules: KnockoutRule[] = [];
  if (req.maxNoticeDays !== undefined && has.has('notice_period_days')) {
    rules.push({ fact: 'notice_period_days', op: 'gt', value: req.maxNoticeDays, severity: 'soft', message: `Notice period over ${req.maxNoticeDays} days` });
  }
  if (req.budgetMaxLpa !== undefined && has.has('expected_ctc_lpa')) {
    rules.push({ fact: 'expected_ctc_lpa', op: 'gt', value: req.budgetMaxLpa, severity: 'soft', message: `Expected CTC above the ${req.budgetMaxLpa} LPA budget` });
  }
  if (req.experience.min > 0 && has.has('relevant_experience_years')) {
    rules.push({
      fact: 'relevant_experience_years',
      op: 'lt',
      value: req.experience.min,
      severity: 'soft',
      message: `Less than ${req.experience.min} year${req.experience.min === 1 ? '' : 's'} of relevant experience`,
    });
  }
  if (has.has('shift_flexible')) rules.push({ fact: 'shift_flexible', op: 'is_false', severity: 'soft', message: 'Not open to shifts' });
  if (has.has('willing_to_relocate')) rules.push({ fact: 'willing_to_relocate', op: 'is_false', severity: 'hard', message: 'Cannot work from the job location' });
  return rules;
}

/** The skills-assessment settings a role starts with: customer-facing roles run role-plays in more languages. */
export function assessmentFor(customerFacing: boolean, tasks: AssessmentTask[]): AssessmentConfig {
  return {
    extraLanguages: customerFacing ? 2 : 0,
    rolePlays: customerFacing ? 3 : 2,
    written: true,
    voiceEngine: 'standard',
    systems: [],
    tasks: clone(tasks),
  };
}

export function fillCompany(text: string, company: string): string {
  return text.replace(/\{company\}/g, company);
}

export function describeJob(roleDescription: string, req: Pick<JobRequirements, 'companyName' | 'aboutCompany'>): string {
  const role = fillCompany(roleDescription, req.companyName);
  return req.aboutCompany ? `${req.aboutCompany.trim()}\n\n${role}` : role;
}

export function standingInstructions(req: Pick<JobRequirements, 'mustHaves'>): string {
  const lines = ['If the candidate asks about salary, say the hiring team discusses it in a later round; never name a figure.'];
  if (req.mustHaves) lines.unshift(`The hiring team especially needs: ${req.mustHaves.trim()} Ask about these in the background section and note what the candidate has.`);
  return lines.join('\n');
}

/**
 * A complete job from a library role and the company's requirements, without AI. Everything stays
 * editable in the job editor before it goes live.
 */
export function buildJobFromRole(role: RoleTemplate, req: JobRequirements): JobInput {
  const shifts = req.shifts ?? role.shifts;
  const common = commonFacts(role.relevantExperience, req, shifts);
  const facts = [...common, ...role.facts.filter((f) => !common.some((c) => c.key === f.key))];
  const own = role.knockouts.filter((k) => facts.some((f) => f.key === k.fact));
  const input = {
    title: req.title,
    department: role.department,
    location: req.location,
    workMode: req.workMode,
    description: describeJob(role.description, req),
    status: 'DRAFT' as const,
    outreachMode: 'WEB_LINK' as const,
    interviewMinutes: 15,
    maxCallAttempts: 3,
    retryAfterMinutes: 120,
    linkValidityHours: 72,
    config: {
      companyName: req.companyName,
      interviewerName: req.interviewerName ?? 'Aria',
      competencies: clone(role.competencies),
      facts,
      knockouts: [...requirementKnockouts(req, facts), ...own],
      thresholds: { qualify: 70, review: 55 },
      languages: req.languages,
      assessEnglish: role.customerFacing,
      experienceRange: req.experience,
      extraInstructions: standingInstructions(req),
      voiceEngine: 'standard' as const,
      assessment: assessmentFor(role.customerFacing, role.tasks),
      messages: defaultJobMessages(),
      apply: defaultApplySettings(),
      roleId: role.id,
    },
  };
  return JobInputSchema.parse(input);
}

const PLACEHOLDER = /\{([a-z_]+)\}/g;
const ALLOWED_PLACEHOLDERS = new Set(['customer', 'org', 'city', 'company']);

/** Everything wrong with a library role, as readable lines (empty when it is fine). Used by the tests. */
export function checkRole(role: RoleTemplate): string[] {
  const problems: string[] = [];
  const say = (m: string) => problems.push(`${role.id}: ${m}`);
  if (!/^[a-z][a-z0-9_]{1,40}$/.test(role.id)) say('id must be lower_snake_case');
  if (role.competencies.length < 4 || role.competencies.length > 6) say(`needs 4-6 competencies, has ${role.competencies.length}`);
  const weights = role.competencies.reduce((s, c) => s + c.weight, 0);
  if (weights !== 100) say(`competency weights sum to ${weights}, not 100`);
  const skills = new Set(role.competencies.map((c) => c.key));
  if (skills.size !== role.competencies.length) say('duplicate competency key');
  for (const f of role.facts) if ((COMMON_FACT_KEYS as readonly string[]).includes(f.key)) say(`fact ${f.key} is a common fact; remove it`);
  const factKeys = new Set(role.facts.map((f) => f.key));
  for (const k of role.knockouts) if (!factKeys.has(k.fact)) say(`knockout on ${k.fact}, which is not one of the role's own facts`);
  const rolePlays = role.tasks.filter((t) => t.kind === 'roleplay').length;
  const written = role.tasks.filter((t) => t.kind === 'written').length;
  if (rolePlays < 3) say(`needs at least 3 role-plays, has ${rolePlays}`);
  if (written < 2) say(`needs at least 2 written tasks, has ${written}`);
  const ids = new Set<string>();
  for (const t of role.tasks) {
    if (ids.has(t.id)) say(`duplicate task id ${t.id}`);
    ids.add(t.id);
    for (const s of t.skills) if (!skills.has(s)) say(`task ${t.id} lists skill ${s}, which is not a competency key`);
    for (const m of JSON.stringify(t).matchAll(PLACEHOLDER)) if (!ALLOWED_PLACEHOLDERS.has(m[1]!)) say(`task ${t.id} uses {${m[1]}}; only {customer}, {org}, {city} and {company}`);
  }
  for (const m of role.description.matchAll(PLACEHOLDER)) if (m[1] !== 'company') say(`description uses {${m[1]}}; only {company}`);
  try {
    buildJobFromRole(role, {
      title: role.title,
      companyName: 'Acme',
      location: 'Pune',
      workMode: 'On-site',
      experience: role.experience,
      budgetMaxLpa: 8,
      maxNoticeDays: 30,
      languages: ['en-IN', 'hi-IN'],
    });
  } catch (err) {
    say(`does not build a valid job: ${err instanceof z.ZodError ? err.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') : String(err)}`);
  }
  return problems;
}
